import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useMeasure from 'react-use-measure';
import cn from 'classnames';
import { motion, type Variants } from 'framer-motion';

import { useSaveAfterBombEventMutation } from '@/api/queries/game';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import { CoinBalanceTopUp } from '@/components/CoinBalanceTopUp/CoinBalanceTopUp';
import { H1, H3, StringSmall } from '@/components/Typography/Typography';
import {
  MotionPurpleButton,
  MotionRedButtton,
} from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import { setActiveBoard } from '@/redux/reducers/diceGameReducer';
import { addToBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { GameItemsRewards } from '../../GameItemsRewards/GameItemsRewards';
import { useCardsGameCollectRewards } from '../../hooks/collect-rewards';
import { Reward } from '../../Reward/Reward';

import {
  bottomBlockAnimationVariants,
  bottomButtonAnimationVariants,
  topBlockAnimationVariants,
  topButtonAnimationVariants,
} from './config';

import css from './styles.module.scss';

import bombPng from '/img/dice-game/bomb-very-huge.png';
import bombWebp from '/img/dice-game/bomb-very-huge.webp';
import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

interface MainViewProps {
  closeBombEvent: () => void;
  changeBombPosition: (args: { x: number; y: number }) => void;
  animationVariant: 'start' | 'end';
  changeAnimationVariant: (varinat: 'start' | 'end') => void;
}

const MainView: FC<MainViewProps> = ({
  closeBombEvent,
  changeBombPosition,
  animationVariant,
  changeAnimationVariant,
}) => {
  const [bombRef, bombMeasure] = useMeasure();
  const tgemBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const { collectRewards, reset } = useCardsGameCollectRewards();
  const { t } = useTranslation();
  const rewardCollected = useAppSelector(
    (state) => state.diceGame.miniGame?.rewardCollected,
  );
  const dispatch = useAppDispatch();
  const currentRollPower = useAppSelector(
    (state) => state.diceGame.currentRollPower,
  );

  const [saveAfterBomb, { isLoading }] = useSaveAfterBombEventMutation();
  const defuseBombPrice = currentRollPower * 5;

  useEffect(() => {
    if (bombMeasure.x && bombMeasure.y) {
      const { x, y } = bombMeasure;
      changeBombPosition({ x, y });
    }
  }, [bombMeasure]);

  const continueMiniGame = (): void => {
    saveAfterBomb()
      .unwrap()
      .then(() => {
        dispatch(addToBalance({ tgem: -1 * defuseBombPrice }));
        SendAnalitics.coinSpend({
          amount: defuseBombPrice,
          type: 'roll_bomb_defuse',
          item_name: 'bomb',
        });
      })
      .catch((error) => {
        console.error(error);
        showToast(t('Something went wrong, please try again'));
      });
    dispatch(setActiveBoard('dice'));
    reset();
    closeBombEvent();
  };

  const takeHit = (): void => {
    changeAnimationVariant('end');
    collectRewards();
    setTimeout(() => {
      closeBombEvent();
    }, 800);
  };

  const mainViewAnimationVariants: Variants = {
    start: { opacity: 1, transition: { delay: 1.4 } },
    end: { opacity: 0, transition: { delay: 0.3, duration: 0.05 } },
  };

  return (
    <motion.div
      className={css.bombEvent}
      initial={{ opacity: 0 }}
      variants={mainViewAnimationVariants}
      animate={animationVariant}
    >
      <motion.div
        initial={{ y: -50 }}
        variants={topBlockAnimationVariants}
        animate={animationVariant}
        className="w-full flex flex-col items-center relative z-[1]"
      >
        <CoinBalanceTopUp
          className="!h-12 !pl-2 !pr-3 py-[0.625rem] mb-[5rem]"
          contenctClassName="!items-start"
          compare={Infinity}
          iconSize="1.75rem"
        />
        <H1
          weight="extrabold"
          className={cn('mb-2 !text-[#FFE6E6]', css.title)}
        >
          {t('Danger ahead!')}
        </H1>
        <StringSmall className="!text-white/80 text-center mb-16 max-w-[17.125rem]">
          {t(
            'You’re on a Bomb Square! You hit a bomb and lose all rewards from this field...',
          )}
        </StringSmall>
      </motion.div>
      <div ref={bombRef} className="relative w-[169px] h-[169px]"></div>
      <motion.div
        initial={{ y: 50 }}
        variants={bottomBlockAnimationVariants}
        animate={animationVariant}
        className="w-full flex flex-col items-center"
      >
        {rewardCollected && (
          <>
            {rewardCollected.cash !== undefined ? (
              <Reward
                kind="Cash"
                value={rewardCollected.cash || 0}
                className="relative z-[2]"
              />
            ) : (
              <GameItemsRewards
                className="!mt-0 relative z-[2]"
                list={Object.entries(rewardCollected || {}).map(
                  ([item, value]) => ({
                    itemName: item as
                      | ResourceTypeText
                      | PlantTypeText
                      | GoodsType,
                    value,
                  }),
                )}
              />
            )}
          </>
        )}
        <div className="flex flex-col items-center w-full relative z-[1] pt-11">
          <StringSmall className="mb-6 !text-white/80">
            {t('...or defuse it and save your run!')}
          </StringSmall>
          <motion.div className="w-[16.5rem] flex flex-col px-4 gap-2">
            <MotionPurpleButton
              initial={{ x: '-50%', y: 'calc(50% + 4px)' }}
              variants={topButtonAnimationVariants}
              animate={animationVariant}
              onClick={continueMiniGame}
              inactive={(tgemBalance || 0) < defuseBombPrice || isLoading}
              disabled={(tgemBalance || 0) < defuseBombPrice || isLoading}
              className="flex items-center gap-1"
            >
              <H3
                weight="extrabold"
                className={cn(css.buttonText, '!text-white/80 mr-1')}
              >
                {t('Defuse for')}
              </H3>
              <Picture
                className="w-6 h-6 shrink-0"
                img={tgemPng}
                webp={tgemWebp}
                alt="tgem"
              />
              <H3
                weight="extrabold"
                className={cn(css.buttonText, '!text-white/80')}
              >
                {defuseBombPrice}
              </H3>
            </MotionPurpleButton>
            <MotionRedButtton
              initial={{ x: '50%', y: 'calc(-50% - 4px)' }}
              variants={bottomButtonAnimationVariants}
              animate={animationVariant}
              onClick={takeHit}
              isLoading={isLoading}
              disabled={isLoading}
              className="flex items-center gap-1"
            >
              <H3
                weight="extrabold"
                className={cn(css.buttonText, '!text-white/80')}
              >
                {t('Take a hit')}
              </H3>
              <Picture
                className="w-6 h-6 shrink-0"
                img={bombPng}
                webp={bombWebp}
                alt="dice"
              />
            </MotionRedButtton>
          </motion.div>
          <div className="w-16 h-16 shrink-0" />
        </div>
      </motion.div>
    </motion.div>
  );
};

export { MainView };
